import { useEffect, useState } from "react";
import configs from "../../configs";
import { handoffToEditorLogin } from "../auth/xrhubAuthHandoff.js";
import { setGetAccessTokenSilently, setLoginWithPopup, setLoginWithRedirect } from "../utils";
import {
  getDashboardBridgeToken,
  loginWithDashboardBridgePopup,
  logoutWithDashboardBridge,
  renewDashboardBridgeToken,
  startDashboardBridgeRehydration,
  startDashboardBridgeRedirect
} from "../auth/dashboardBridgeAuth.js";
import { readAuthHint } from "../auth/authHint.js";

async function redirectToEditorLogin(location = window.location) {
  await handoffToEditorLogin({
    location,
    xrhubUrl: configs.XRHUB_URL,
    creatorBasePath: configs.ROUTER_BASE_PATH,
    auth0Redirect: configs.AUTH0_REDIRECT,
    useLocalLogin: false,
    persistReturnTo: returnTo => localStorage.setItem("returnTo", returnTo),
    navigate: url => window.location.replace(url)
  });
}

export const useAuth0 = () => {
  const initialContext = {
    loading: true,
    isAuthenticated: false,
    user: {},
    popupOpen: false,
    accessToken: "",
    error: null,
    loginWithRedirect: () => {},
    getIdTokenClaims: () => {},
    getTokenSilently: () => {},
    getTokenWithPopup: () => {},
    auth0Logout: () => {},
    getAccessTokenSilently: () => {},
    handleRedirectCallback: () => {}
  };

  const [context, setContext] = useState(initialContext);

  useEffect(() => {
    const initAuth0 = async () => {
      const clearStoredToken = () => {
        localStorage.removeItem("auth0token");
      };
      const resolveReturnTo = returnTo => {
        if (!returnTo) {
          return window.location.href;
        }

        try {
          return new URL(returnTo, window.location.origin).href;
        } catch (_error) {
          return window.location.href;
        }
      };
      const bridgeGetToken = async () => (await renewDashboardBridgeToken()) || "";
      const bridgeLoginWithRedirect = async options => {
        await startDashboardBridgeRedirect(resolveReturnTo(options?.appState?.returnTo));
      };
      const bridgeLoginWithPopup = async () => {
        const token = await loginWithDashboardBridgePopup(window.location.href);
        if (token) {
          clearStoredToken();
        }
      };
      const bridgeLogout = (options = {}) => {
        logoutWithDashboardBridge({
          logoutParams: {
            returnTo: resolveReturnTo(options.logoutParams?.returnTo)
          }
        });
      };

      setGetAccessTokenSilently(bridgeGetToken);
      setLoginWithPopup(bridgeLoginWithPopup);
      setLoginWithRedirect(bridgeLoginWithRedirect);

      const authHint = readAuthHint();
      const accessToken = (await renewDashboardBridgeToken().catch(() => null)) || getDashboardBridgeToken() || "";

      if (!accessToken) {
        if (authHint) {
          const didStartRehydration = await startDashboardBridgeRehydration(window.location.href);
          if (didStartRehydration) {
            return;
          }
        }

        if (configs.isLocal) {
          await startDashboardBridgeRedirect(window.location.href);
        } else {
          await redirectToEditorLogin(window.location);
        }
        return;
      }

      clearStoredToken();

      setContext(state => ({
        ...state,
        loading: false,
        isAuthenticated: true,
        accessToken,
        loginWithRedirect: bridgeLoginWithRedirect,
        loginWithPopup: bridgeLoginWithPopup,
        auth0Logout: bridgeLogout,
        getAccessTokenSilently: bridgeGetToken,
        getTokenSilently: bridgeGetToken,
        getTokenWithPopup: async () => await loginWithDashboardBridgePopup(window.location.href),
        getIdTokenClaims: async () => ({}),
        handleRedirectCallback: async () => {}
      }));
    };

    initAuth0();
  }, []);

  return context;
};
